import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { flattenArray } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { createRef, forwardRef, useContext, useImperativeHandle, useRef } from 'react';
import { useOccContext } from '../../../../../context/occ/hooks/use-occ-context';
import { DropdownContextInstance } from '../types/context';
import { getIdForDropdownButton, getIdForDropdownList, getIdForDropdownListItem } from '../utils';
import { DropdownGroup } from './group';
var DropdownListPosition;
(function (DropdownListPosition) {
    DropdownListPosition["Above"] = "Above";
    DropdownListPosition["Below"] = "Below";
})(DropdownListPosition || (DropdownListPosition = {}));
const b = bemClassBuilder('v-dropdown-list');
const DropdownListBase = (_props, ref) => {
    const { i18n, displayText: occDisplayText } = useOccContext();
    const listRef = useRef(null);
    const groupRefs = {};
    const { containerRef, dropdownId, filteredOptions: options, optionGroups, selectedOption, displayAsModalOnMobile, showModalErrorMessage, isDropdownExpanded, noSearchResultsFoundMessage, searchTerm } = useContext(DropdownContextInstance);
    const scrollPageUp = () => {
        const listRefElement = listRef.current;
        if (!listRefElement || !isDropdownExpanded)
            return;
        const currentTop = listRefElement.scrollTop;
        const minTop = 0;
        const newTop = currentTop - listRefElement.offsetHeight;
        listRefElement.scrollTo({ top: newTop > minTop ? newTop : minTop });
    };
    const scrollPageDown = () => {
        const listRefElement = listRef.current;
        if (!listRefElement || !isDropdownExpanded)
            return;
        const currentTop = listRefElement.scrollTop;
        const maxTop = listRefElement.scrollHeight - listRefElement.offsetHeight;
        const newTop = currentTop + listRefElement.offsetHeight;
        listRefElement.scrollTo({ top: newTop < maxTop ? newTop : maxTop });
    };
    const scrollOptionIntoView = (value) => {
        var _a;
        const listRefElement = listRef.current;
        const groupRef = groupRefs[value];
        if (!listRefElement || !groupRef)
            return;
        (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.scrollOptionIntoView(value);
    };
    const getHeight = () => {
        const listRefElement = listRef.current;
        return listRefElement ? listRefElement.offsetHeight : 0;
    };
    const getPosition = () => {
        const listRefElement = listRef.current;
        if (!containerRef.current || !listRefElement)
            return DropdownListPosition.Below;
        const windowHeight = window.innerHeight;
        const containerYRelativeToWindow = containerRef.current.getBoundingClientRect().top;
        const containerHeight = containerRef.current.offsetHeight;
        const listHeight = getHeight();
        const fitsBelow = containerYRelativeToWindow + containerHeight + listHeight <= windowHeight;
        const fitsAbove = containerYRelativeToWindow - listHeight > 0;
        if (!fitsBelow && fitsAbove)
            return DropdownListPosition.Above;
        return DropdownListPosition.Below;
    };
    useImperativeHandle(ref, () => ({
        scrollPageDown,
        scrollPageUp,
        scrollOptionIntoView,
        getHeight
    }));
    const ariaProps = {
        'aria-labelledby': getIdForDropdownButton(dropdownId),
        'aria-activedescendant': selectedOption ? getIdForDropdownListItem(dropdownId, selectedOption) : undefined
    };
    const hasOptions = options.length > 0;
    const groupedOptions = optionGroups.map(group => group.optionValues).reduce(flattenArray, []);
    const ungroupedOptions = options.filter(option => !(groupedOptions === null || groupedOptions === void 0 ? void 0 : groupedOptions.includes(option.value)));
    optionGroups.unshift({
        optionValues: ungroupedOptions.map(o => o.value)
    });
    // TODO: Styling for 'no options'. #286592
    return (_jsxs("ul", Object.assign({ className: b.block({
            position: getPosition(),
            inMobileModal: !!displayAsModalOnMobile,
            hideInMobile: showModalErrorMessage
        }), id: getIdForDropdownList(dropdownId), tabIndex: -1, role: "listbox" }, ariaProps, { ref: listRef }, { children: [!hasOptions && (_jsx("li", Object.assign({ className: b.element('no-results') }, { children: getNoSearchResultsFoundMessage(noSearchResultsFoundMessage || occDisplayText.noSearchResultsFoundMessage, i18n, searchTerm) }))), optionGroups.map((optionGroup, i) => {
                const validOptionGroupValues = optionGroup.optionValues.filter(val => !!options.find(o => o.value === val));
                const groupOptions = validOptionGroupValues.map(val => options.find(o => o.value === val));
                const label = optionGroup.label;
                groupRefs[label] = createRef();
                return _jsx(DropdownGroup, { ref: groupRefs[label], label: label, options: groupOptions }, `group_${i}`);
            })] })));
};
export const DropdownList = observer(forwardRef(DropdownListBase));
DropdownList.displayName = 'DropdownList';
export const getNoSearchResultsFoundMessage = (template, i18n, searchTerm) => {
    return i18n.template(template, { searchTerm: searchTerm || '' });
};
